import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, Users, CheckCircle2, XCircle, TrendingUp, MapPin } from "lucide-react";

const STATUS = {
  aguardando_pagamento: { label: "Abandonada", cls: "bg-aj-surface-2 text-aj-text-muted" },
  pago: { label: "Paga · aguardando pedido", cls: "bg-aj-amber-soft text-aj-orange-hover" },
  liberado: { label: "Contatos liberados", cls: "bg-aj-success text-white" },
};

export default function ConexoesParticulares() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/conexoes-particulares")
      .then(r => setData(r.data))
      .catch(e => toast.error(formatApiError(e)));
  }, []);

  if (!data) return <div className="p-12 text-center text-aj-text-muted">Carregando…</div>;
  const { resumo, conexoes } = data;

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Financeiro SaaS · Cliente Particular</div>
        <h1 className="font-display font-extrabold text-3xl text-aj-blue-deep mt-1">Conexões Particulares</h1>
        <p className="text-sm text-aj-text-muted mt-1">Receita das taxas de R$ 10,00 e conversão do fluxo público de busca de montadores.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={<Wallet size={18} />} label="Receita (pagas)" v={`R$ ${resumo.receita.toFixed(2)}`} destaque testid="cp-receita" />
        <Kpi icon={<CheckCircle2 size={18} />} label="Conexões pagas" v={resumo.pagas} testid="cp-pagas" />
        <Kpi icon={<TrendingUp size={18} />} label="Conversão" v={`${resumo.conversao_pct}%`} testid="cp-conversao" />
        <Kpi icon={<XCircle size={18} />} label="Abandonadas" v={resumo.abandonadas} testid="cp-abandonadas" />
      </div>

      {resumo.top_regioes.length > 0 && (
        <div className="aj-card p-5" data-testid="cp-regioes">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-aj-blue-dark mb-3">
            <MapPin size={14} className="text-aj-orange" /> Regiões mais buscadas (pagas)
          </div>
          <div className="flex flex-wrap gap-2">
            {resumo.top_regioes.map(r => (
              <span key={r.regiao} className="px-3 py-1.5 rounded-lg bg-aj-surface-2 text-sm font-semibold text-aj-text">
                {r.regiao} <span className="font-mono text-aj-orange font-bold ml-1">{r.qtd}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="aj-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-aj-surface-2 border-b border-[#E2E8F0]">
            <tr><Th>Data</Th><Th>Região</Th><Th>Cliente</Th><Th>Produto</Th><Th>Montadores</Th><Th>Valor</Th><Th>Status</Th></tr>
          </thead>
          <tbody>
            {conexoes.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center text-aj-text-muted">
                Nenhuma conexão ainda. O fluxo público fica em <span className="font-mono">/montadores</span>.
              </td></tr>
            )}
            {conexoes.map(c => {
              const st = STATUS[c.status] || { label: c.status, cls: "bg-aj-surface-2 text-aj-text" };
              return (
                <tr key={c.id} className="border-b border-[#E2E8F0] last:border-0 hover:bg-aj-surface transition-colors" data-testid={`cp-row-${c.id}`}>
                  <Td className="text-xs whitespace-nowrap">{new Date(c.created_at).toLocaleString("pt-BR")}</Td>
                  <Td><strong>{c.cidade}</strong>/{c.estado}{c.bairro ? ` · ${c.bairro}` : ""}</Td>
                  <Td>{c.pedido?.nome || <span className="text-aj-text-muted">—</span>}</Td>
                  <Td className="text-xs">{c.pedido?.produto || "—"}{c.pedido?.tipo_servico ? ` (${c.pedido.tipo_servico})` : ""}</Td>
                  <Td className="font-mono text-center">{c.montadores?.length || c.total_cidade || 0}</Td>
                  <Td className="font-mono font-bold">{["pago","liberado"].includes(c.status) ? `R$ ${(c.valor || 10).toFixed(2)}` : "—"}</Td>
                  <Td><span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${st.cls}`}>{st.label}</span></Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ icon, label, v, destaque, testid }) {
  return (
    <div className={`aj-card p-5 ${destaque ? "border-l-4 border-l-[#FF6700]" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-aj-orange">{icon}
        <span className="text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">{label}</span>
      </div>
      <div className="font-display font-extrabold text-2xl text-aj-blue-deep mt-2">{v}</div>
    </div>
  );
}
function Th({ children }) { return <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">{children}</th>; }
function Td({ children, className="" }) { return <td className={`p-3 ${className}`}>{children}</td>; }
